/**
 * REST interface editor (ADR-0051, the single exposure registry) — the app's
 * `kind="rest"` interface holds a curated list of ENDPOINTS, each over a table
 * (CRUD per-verb), a dataset (`list`/`get`) or a named Query. This is the ONE
 * place REST exposure is configured; the router + OpenAPI read from here.
 *
 * MASTER/DETAIL (WS-8, like `AppQueriesPane`): a `view` prop drives whether we
 * render the endpoint LIST (grouped by resource, in the builder's lateral
 * sidebar) or the selected endpoint's DETAIL editor (full-width in the main
 * pane), selection lifted to the caller. `RestApiDocs` is a SEPARATE surface
 * (its own rail icon) — a Scalar/Swagger-style reference.
 *
 * The RESOURCE both groups endpoints AND defines the endpoint's PATH ROOT
 * (`path = /<slug(resource)><subpath>`). Endpoints persist immediately per
 * create/delete; the detail has an explicit "Salva".
 *
 * Admin console section — native `<select>` is acceptable (repo convention).
 * i18n: inline `t(key, fallback)` with Italian fallbacks.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BookText, ChevronDown, ChevronRight, Globe, Play, Plus, Search, Trash2 } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuthStore } from "@/lib/auth";
import { appsApi, type QueryParam } from "@/lib/api-apps";
import {
  allowedVerbs,
  composePath,
  emptyEndpoint,
  endpointRoutes,
  endpointsOf,
  firstParamError,
  firstRouteConflict,
  groupByResource,
  PARAM_LOCATIONS,
  PARAM_TYPES,
  pathPlaceholders,
  resourceRoot,
  resourceSubpath,
  type ParamLocation,
  type RestEndpoint,
  type RestParam,
  type RestSource,
  type RestVerb,
} from "@/lib/restEndpoints";

const SELECT = "h-9 w-full rounded-md border border-border bg-surface px-2 text-sm";
export type RestView = "list" | "detail" | "full";
const METHOD_TINT: Record<string, string> = {
  GET: "bg-info/15 text-info",
  POST: "bg-success/15 text-success",
  PUT: "bg-warning/15 text-warning",
  PATCH: "bg-warning/15 text-warning",
  DELETE: "bg-danger/15 text-danger",
};

type Opt = { id: string; label: string };

function useRest(appId: string) {
  const qc = useQueryClient();
  const ifacesQ = useQuery({ queryKey: ["apps", appId, "interfaces"], queryFn: () => appsApi.listInterfaces(appId) });
  const iface = (ifacesQ.data ?? []).find((i) => i.kind === "rest") ?? null;
  const rows = React.useMemo(() => endpointsOf(iface?.config), [iface?.config]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["apps", appId, "interfaces"] });
  const create = useMutation({
    mutationFn: () =>
      appsApi.createInterface(appId, { kind: "rest", name: "REST API", config: { endpoints: [] }, standalone: true, embedded: false }),
    onSuccess: invalidate,
  });
  const writeEndpoints = useMutation({
    mutationFn: (endpoints: RestEndpoint[]) => appsApi.updateInterface(appId, iface!.id, { config: { endpoints } }),
    onSuccess: invalidate,
  });
  // Presence toggle (assistant-interface pattern): flip the whole interface on/off.
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => appsApi.updateInterface(appId, iface!.id, { enabled }),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: () => appsApi.deleteInterface(appId, iface!.id),
    onSuccess: invalidate,
  });
  return { loading: ifacesQ.isLoading, iface, rows, create, writeEndpoints, toggle, del };
}

function useOptions(appId: string) {
  const tablesQ = useQuery({ queryKey: ["apps", appId, "tables"], queryFn: () => appsApi.listTables(appId) });
  const datasetsQ = useQuery({ queryKey: ["apps", appId, "datasets"], queryFn: () => appsApi.listDatasets(appId) });
  const queriesQ = useQuery({ queryKey: ["apps", appId, "queries"], queryFn: () => appsApi.listQueries(appId) });
  const rolesQ = useQuery({ queryKey: ["apps", appId, "roles"], queryFn: () => appsApi.listRoles(appId) });
  return {
    tables: (tablesQ.data ?? []).map((x) => ({ id: x.id, label: x.name })),
    datasets: (datasetsQ.data ?? []).map((x) => ({ id: x.id, label: x.name })),
    queries: (queriesQ.data ?? []).map((x) => ({ id: x.id, label: `${x.name} (${x.key})` })),
    roles: (rolesQ.data ?? []).map((x) => ({ id: x.id, label: x.name })),
    // The full Query rows so an endpoint can BIND its params to the referenced
    // Query's declared input contract (ADR-0054) — keyed by id.
    queriesById: Object.fromEntries((queriesQ.data ?? []).map((x) => [x.id, x])),
  };
}

export function RestInterfaceEditor({
  appId,
  canWrite,
  view = "full",
  selectedId,
  onSelectId,
}: {
  appId: string;
  canWrite: boolean;
  view?: RestView;
  selectedId?: string | null;
  onSelectId?: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  const rest = useRest(appId);
  const opts = useOptions(appId);
  const [innerSel, setInnerSel] = React.useState<string | null>(null);
  const sel = view === "full" ? innerSel : selectedId ?? null;
  const setSel = view === "full" ? setInnerSel : onSelectId ?? (() => {});

  if (rest.loading) return <div className="p-4 text-sm text-muted-foreground">…</div>;

  const exists = !!rest.iface;
  const enabled = rest.iface?.enabled ?? false;

  const createEndpoint = () => {
    if (!rest.iface) return;
    const ep = emptyEndpoint("table");
    rest.writeEndpoints.mutate([...rest.rows, ep], { onSuccess: () => setSel(ep.id) });
  };

  // The Abilitato toggle governs PRESENCE (assistant-interface pattern, ADR-0049):
  // with no interface yet, turning it ON creates it; otherwise it flips `enabled`.
  // Disabling keeps it configured but hidden — there is no separate "create" step.
  const setPresence = (on: boolean) => {
    if (!rest.iface) {
      if (on) rest.create.mutate();
      return;
    }
    rest.toggle.mutate(on);
  };
  const removeInterface = () => {
    if (!rest.iface) return;
    if (window.confirm(t("apps.restDeleteConfirm", "Eliminare l'interfaccia REST e tutti i suoi endpoint?")))
      rest.del.mutate(undefined, { onSuccess: () => setSel(null) });
  };
  const busy = rest.create.isPending || rest.toggle.isPending || rest.del.isPending;

  const list = (
    <RestList
      exists={exists}
      enabled={enabled}
      busy={busy}
      rows={rest.rows}
      canWrite={canWrite}
      selectedId={sel}
      onSelect={setSel}
      conflict={exists ? firstRouteConflict(rest.rows) : null}
      onCreate={createEndpoint}
      onTogglePresence={setPresence}
      onDelete={removeInterface}
    />
  );

  const currentEp = sel ? rest.rows.find((e) => e.id === sel) ?? null : null;
  const detail = exists ? (
    <RestDetail
      key={currentEp?.id ?? "none"}
      ep={currentEp}
      canWrite={canWrite}
      opts={opts}
      allRows={rest.rows}
      saving={rest.writeEndpoints.isPending}
      onCreate={createEndpoint}
      onSave={(draft) => rest.writeEndpoints.mutate(rest.rows.map((e) => (e.id === draft.id ? draft : e)))}
      onDelete={(id) => rest.writeEndpoints.mutate(rest.rows.filter((e) => e.id !== id), { onSuccess: () => setSel(null) })}
    />
  ) : (
    <InterfaceInactive
      icon={Globe}
      title={t("apps.restInterface", "API REST")}
      hint={t(
        "apps.restInactiveHint",
        "Esponi tabelle, proiezioni e Query di questa app via HTTP. Attiva l'interruttore «Abilitato» a sinistra per crearla, poi aggiungi gli endpoint.",
      )}
    />
  );

  // Surface a save failure — the interface config is validated server-side on every
  // write, so a bad endpoint (e.g. an unknown verb, a colliding path) 422s the whole
  // save; without this the mutation error was swallowed and the UI silently did nothing.
  const writeErr = rest.writeEndpoints.error ? (
    <Alert tone="danger" title={t("apps.restSaveFailed", "Salvataggio non riuscito")}>
      {String((rest.writeEndpoints.error as Error)?.message ?? rest.writeEndpoints.error)}
    </Alert>
  ) : null;

  if (view === "list")
    return (
      <>
        {writeErr}
        {list}
      </>
    );
  if (view === "detail")
    return (
      <>
        {writeErr}
        {detail}
      </>
    );
  return (
    <div className="space-y-3">
      {writeErr}
      <div className="grid min-h-[520px] grid-cols-[16rem_1fr] gap-4">
        <div className="rounded-lg border border-border p-2">{list}</div>
        <div>{detail}</div>
      </div>
    </div>
  );
}

/** The endpoint MASTER list — grouped by resource, with a prominent create button. */
function RestList({
  exists,
  enabled,
  busy,
  rows,
  canWrite,
  selectedId,
  onSelect,
  conflict,
  onCreate,
  onTogglePresence,
  onDelete,
}: {
  exists: boolean;
  enabled: boolean;
  busy: boolean;
  rows: RestEndpoint[];
  canWrite: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  conflict: string | null;
  onCreate: () => void;
  onTogglePresence: (on: boolean) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");
  // Filter by path/method/resource (same shape as the Query pane's search).
  const q = search.trim().toLowerCase();
  const visible = q
    ? rows.filter((ep) =>
        `${ep.path} ${ep.resource ?? ""} ${endpointRoutes(ep).map((r) => r.method).join(" ")}`
          .toLowerCase()
          .includes(q),
      )
    : rows;
  const groups = groupByResource(visible);
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Interface header — title + Elimina, then the Abilitato toggle that GOVERNS
          PRESENCE (assistant-interface pattern): no separate "create" step. */}
      <div className="mb-2 shrink-0 space-y-2">
        <div className="flex items-center justify-between gap-2 px-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("apps.restInterface", "API REST")}
          </span>
          {exists && canWrite && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              title={t("common.delete", "Elimina")}
              className="rounded p-0.5 text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
        <label className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface/40 px-2 py-1.5">
          <span className="flex min-w-0 flex-col">
            <span className="text-[12px] font-medium">{t("common.enabled", "Abilitato")}</span>
            <span className="text-[10px] text-muted-foreground">
              {exists
                ? t("apps.restEnabledHint", "Disattiva per nascondere l'API mantenendo la configurazione.")
                : t("apps.restEnableToCreate", "Attiva per creare l'interfaccia REST.")}
            </span>
          </span>
          <Switch
            checked={exists && enabled}
            disabled={!canWrite || busy}
            onCheckedChange={onTogglePresence}
          />
        </label>
      </div>

      {!exists ? (
        <p className="px-1 py-2 text-[11px] text-muted-foreground">
          {t("apps.restNotCreated", "Nessuna interfaccia REST. Attiva l'interruttore qui sopra per crearla.")}
        </p>
      ) : (
        <>
          {canWrite && (
            <button
              type="button"
              onClick={onCreate}
              className="mb-1 flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("apps.restAddEndpoint", "Nuovo endpoint")}
            </button>
          )}
          {rows.length > 5 && (
            <input
              className="mb-1 rounded border border-border bg-background px-1.5 py-1 text-[11px]"
              placeholder={t("common.search", "Cerca…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          {conflict && (
            <div className="mb-1 rounded border border-danger/40 bg-danger/5 px-1.5 py-1 text-[10px] text-danger">
              {t("apps.restConflict", "Conflitto")}: <span className="font-mono">{conflict}</span>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {rows.length === 0 ? (
              <p className="px-1 py-2 text-[11px] text-muted-foreground">
                {t("apps.restEmptyList", "Nessun endpoint. Creane uno con «Nuovo endpoint».")}
              </p>
            ) : visible.length === 0 ? (
              <p className="px-1 py-2 text-[11px] text-muted-foreground">{t("common.noResults", "Nessun risultato.")}</p>
            ) : (
              groups.map((g) => (
                <div key={g.resource} className="mt-1">
                  <div className="px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70">{g.resource}</div>
                  {g.endpoints.map((ep) => (
                    <EndpointRow key={ep.id} ep={ep} active={ep.id === selectedId} onClick={() => onSelect(ep.id)} />
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** The DETAIL-column placeholder shown when the interface isn't created yet — fills
 *  the pane (no black void) and points to the Abilitato toggle in the list. */
function InterfaceInactive({
  icon: Icon,
  title,
  hint,
}: {
  icon: typeof Globe;
  title: string;
  hint: string;
}) {
  return (
    <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 px-6 text-center">
      <Icon className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
      <div className="text-sm font-semibold text-muted-foreground">{title}</div>
      <p className="max-w-sm text-xs text-muted-foreground/80">{hint}</p>
    </div>
  );
}

function EndpointRow({ ep, active, onClick }: { ep: RestEndpoint; active: boolean; onClick: () => void }) {
  const methods = Array.from(new Set(endpointRoutes(ep).map((r) => r.method)));
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mb-0.5 flex w-full flex-col gap-0.5 rounded px-1.5 py-1 text-left ${active ? "bg-accent/15 text-accent" : "hover:bg-surface"}`}
    >
      <span className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${ep.enabled ? "bg-success" : "bg-muted-foreground/40"}`} />
        <span className="min-w-0 flex-1 truncate font-mono text-[11px]">{ep.path}</span>
      </span>
      <span className="flex flex-wrap gap-1 pl-3">
        {methods.map((m) => (
          <span key={m} className={`rounded px-1 text-[8px] font-semibold uppercase ${METHOD_TINT[m] ?? "bg-muted text-muted-foreground"}`}>{m}</span>
        ))}
      </span>
    </button>
  );
}

/** The endpoint DETAIL editor — full-width, sectioned (local draft + explicit Salva). */
function RestDetail({
  ep,
  canWrite,
  opts,
  allRows,
  saving,
  onCreate,
  onSave,
  onDelete,
}: {
  ep: RestEndpoint | null;
  canWrite: boolean;
  opts: ReturnType<typeof useOptions>;
  allRows: RestEndpoint[];
  saving: boolean;
  onCreate: () => void;
  onSave: (draft: RestEndpoint) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState<RestEndpoint | null>(ep);
  React.useEffect(() => setDraft(ep), [ep]);

  if (!draft) {
    return (
      <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        <span>{t("apps.restPickEndpoint", "Seleziona un endpoint dalla lista.")}</span>
        {canWrite && (
          <Button variant="outline" onClick={onCreate}>
            <Plus className="mr-1.5 h-4 w-4" strokeWidth={2} /> {t("apps.restAddEndpoint", "Nuovo endpoint")}
          </Button>
        )}
      </div>
    );
  }

  const patch = (up: Partial<RestEndpoint>) => setDraft((d) => (d ? { ...d, ...up } : d));
  const optionsFor = (s: RestSource): Opt[] => (s === "table" ? opts.tables : s === "dataset" ? opts.datasets : opts.queries);
  const paramError = firstParamError(draft);
  const conflict = firstRouteConflict([...allRows.filter((e) => e.id !== draft.id), draft]);
  const root = resourceRoot(draft.resource);
  const subpath = resourceSubpath(draft.path, draft.resource);

  const setResource = (resource: string) => patch({ resource: resource || null, path: composePath(resource, resourceSubpath(draft.path, draft.resource)) });
  // A query key is NOT part of the route template (the backend path can't hold a `?`). So if
  // the operator types `?{foo}` in the URL, strip it from the stored path and MATERIALIZE a
  // query param `foo` (its name is a free `?key`). The path stays a valid, routable template.
  const setSubpath = (sub: string) => {
    const qIdx = sub.indexOf("?");
    const pathPart = qIdx >= 0 ? sub.slice(0, qIdx) : sub;
    const newPath = composePath(draft.resource, pathPart);
    if (draft.source !== "query" || qIdx < 0) {
      patch({ path: newPath });
      return;
    }
    const declared = Array.from(sub.slice(qIdx + 1).matchAll(/\{([a-z][a-z0-9_]*)\}/g), (m) => m[1]);
    const existing = draft.params ?? [];
    const toAdd = declared
      .filter((n) => !existing.some((p) => p.name === n))
      .map((n) => ({ name: n, location: "query" as ParamLocation, type: "string" as const, required: false, maps_to: null }));
    patch({ path: newPath, ...(toAdd.length ? { params: [...existing, ...toAdd] } : {}) });
  };
  // The full URL preview reconstructs the query keys (which live in the params, not the path).
  const queryKeys = (draft.params ?? []).filter((p) => p.location === "query").map((p) => p.name).filter(Boolean);
  const fullUrl = draft.path + (queryKeys.length ? `?${queryKeys.map((k) => `{${k}}`).join("&")}` : "");
  const changeSource = (source: RestSource) =>
    patch({ source, target_id: "", verbs: source === "query" ? [] : [...allowedVerbs(source)], method: source === "query" ? "POST" : null, params: [] });
  const toggleVerb = (v: RestVerb) => patch({ verbs: draft.verbs.includes(v) ? draft.verbs.filter((x) => x !== v) : [...draft.verbs, v] });
  const methods = Array.from(new Set(endpointRoutes(draft).map((r) => r.method)));

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Sticky header — path + methods + actions. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {methods.map((m) => (
              <span key={m} className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${METHOD_TINT[m] ?? "bg-muted text-muted-foreground"}`}>{m}</span>
            ))}
          </div>
          <span className="truncate font-mono text-sm font-semibold">{draft.path}</span>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {t("common.enabled", "Abilitato")}
          <Switch checked={draft.enabled} disabled={!canWrite} onCheckedChange={(v) => patch({ enabled: v })} />
        </label>
        {canWrite && (
          <>
            <Button size="sm" onClick={() => onSave(draft)} disabled={saving || !!conflict || !!paramError}>
              {t("common.save", "Salva")}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(draft.id)} title={t("common.delete", "Elimina")}>
              <Trash2 className="h-4 w-4 text-danger" strokeWidth={1.5} />
            </Button>
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pt-4">
        {conflict && (
          <Alert tone="danger" title={t("apps.restConflict", "Conflitto di rotta")}>
            <span className="font-mono text-xs">{conflict}</span>
          </Alert>
        )}
        {paramError && !conflict && (
          <Alert tone="danger" title={t("apps.restParamError", "Parametri non validi")}>
            <span className="font-mono text-xs">{paramError}</span>
          </Alert>
        )}

        {/* 1. Sorgente — the fundamental choice (what data), so it comes first. */}
        <Section title={t("apps.restSecSource", "Sorgente dati")}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={t("apps.restSource", "Sorgente")}>
              <select className={SELECT} value={draft.source} disabled={!canWrite} onChange={(e) => changeSource(e.target.value as RestSource)}>
                <option value="table">{t("apps.restSourceTable", "Tabella")}</option>
                <option value="dataset">{t("apps.restSourceDataset", "Proiezione")}</option>
                <option value="query">{t("apps.restSourceQuery", "Query")}</option>
              </select>
            </Field>
            <Field label={t("apps.restTarget", "Destinazione")}>
              <select
                className={SELECT}
                value={draft.target_id}
                disabled={!canWrite}
                onChange={(e) => {
                  const opt = optionsFor(draft.source).find((o) => o.id === e.target.value);
                  // Changing the target = a new contract → drop the param bindings + the
                  // input_map so the ParamsEditor re-seeds from the new Query (ADR-0054/0057).
                  const reset = draft.source === "query" ? { params: [] as RestParam[], input_map: null } : {};
                  if (!draft.resource && opt) {
                    // No resource yet → seed it from the target label + reset to its root.
                    patch({ target_id: e.target.value, resource: opt.label, path: composePath(opt.label, ""), ...reset });
                  } else {
                    patch({ target_id: e.target.value, ...reset });
                  }
                }}
              >
                <option value="">{t("apps.restPickTarget", "— scegli —")}</option>
                {optionsFor(draft.source).map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </Field>
          </div>
        </Section>

        {/* 2. Percorso e metodo — where it's reachable + which HTTP verbs. */}
        <Section title={t("apps.restSecPath", "Percorso e metodo")}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label={t("apps.restResource", "Risorsa (gruppo + radice)")} hint={root ? `${t("apps.restRootIs", "Radice")}: ${root}` : t("apps.restRootNone", "Nessuna radice — usa il percorso completo")}>
              <Input className="h-9" value={draft.resource ?? ""} disabled={!canWrite} placeholder={t("apps.restResourcePlaceholder", "es. Aziende")} onChange={(e) => setResource(e.target.value)} />
            </Field>
            <Field label={t("apps.restSubpath", "Sotto-percorso")} className="md:col-span-2" hint={t("apps.restPathIs", "Percorso completo:") + " " + fullUrl}>
              <div className="flex items-center gap-0 rounded-md border border-border bg-surface">
                {root && <span className="shrink-0 whitespace-nowrap px-2 font-mono text-xs text-muted-foreground">{root}</span>}
                {draft.source === "query" && (
                  <select className="h-9 border-l border-border bg-transparent px-1.5 text-xs" value={draft.method ?? "POST"} disabled={!canWrite} onChange={(e) => patch({ method: e.target.value as RestEndpoint["method"] })}>
                    {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                )}
                <input
                  className="h-9 min-w-0 flex-1 bg-transparent px-2 font-mono text-sm outline-none"
                  value={subpath}
                  disabled={!canWrite}
                  onChange={(e) => setSubpath(e.target.value)}
                  placeholder={draft.source === "query" ? "/azione/{id}" : t("apps.restSubpathPh", "(vuoto = radice)")}
                />
              </div>
            </Field>
          </div>
          {draft.source !== "query" && (
            <Field label={t("apps.restVerbs", "Metodi HTTP")} hint={t("apps.restVerbsHint", "Ogni verbo genera la sua rotta (list/get/create/update/delete).")}>
              <div className="flex flex-wrap items-center gap-4 pt-0.5">
                {allowedVerbs(draft.source).map((v) => (
                  <label key={v} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={draft.verbs.includes(v)} disabled={!canWrite} onChange={() => toggleVerb(v)} />
                    <span className="font-mono">{v}</span>
                  </label>
                ))}
              </div>
            </Field>
          )}
        </Section>

        {/* 3. Parametri — only a QUERY endpoint has an input contract to map. */}
        {draft.source === "query" && (
          <Section title={t("apps.restParams", "Parametri (path · query · body)")}>
            <ParamsEditor
              ep={draft}
              canWrite={canWrite}
              onPatch={patch}
              queryInputs={draft.target_id ? opts.queriesById[draft.target_id]?.input_schema ?? null : null}
            />
          </Section>
        )}

        {/* 4. Sicurezza */}
        <Section title={t("apps.restSecSecurity", "Sicurezza")}>
          <Field label={t("apps.restRunAs", "Esegui come ruolo")} hint={t("apps.restRunAsHint", "Un ruolo applica la sua RLS + guardia di scrittura — la chiamata non può superare il ruolo. Vuoto = accesso pieno.")}>
            <select className={SELECT} value={draft.run_as_role_id ?? ""} disabled={!canWrite} onChange={(e) => patch({ run_as_role_id: e.target.value || null })}>
              <option value="">{t("apps.restRunAsNone", "Operatore (senza restrizioni)")}</option>
              {opts.roles.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </Field>
        </Section>

        {/* 5. Descrizione — optional documentation, last (least critical). */}
        <Section title={t("apps.restDescription", "Descrizione")}>
          <textarea
            className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
            rows={2}
            value={draft.description ?? ""}
            disabled={!canWrite}
            placeholder={t("apps.restDescriptionPh", "A cosa serve questo endpoint (mostrato nella documentazione)")}
            onChange={(e) => patch({ description: e.target.value || null })}
          />
        </Section>
      </div>
    </div>
  );
}

/** The parameters of a QUERY endpoint (ADR-0057). The operator DEFINES the endpoint's own
 *  HTTP input params — each with a name + location (path · query · body) + type — and MAPS
 *  each ARBITRARILY onto a Query input (`maps_to`). The referenced Query's declared inputs
 *  (`queryInputs`) are just the set of valid mapping TARGETS (the dropdown), NOT the list
 *  itself. A path param's name is a `{placeholder}` in the URL, so it's picked from the URL's
 *  detected placeholders (guaranteeing the path contract matches). No hidden reconcile —
 *  what you define is what's stored. */
function ParamsEditor({
  ep,
  canWrite,
  onPatch,
  queryInputs,
}: {
  ep: RestEndpoint;
  canWrite: boolean;
  onPatch: (up: Partial<RestEndpoint>) => void;
  queryInputs: QueryParam[] | null;
}) {
  const { t } = useTranslation();
  const params = ep.params ?? [];
  // Path placeholders come ONLY from the path part (before any `?`); a query key is not a
  // route placeholder. The stored path never holds a `?`, this is just defensive.
  const placeholders = pathPlaceholders(ep.path.split("?")[0]);
  const inputNames = (queryInputs ?? []).map((q) => q.name);
  // Body params form the JSON request body — only meaningful on verbs that carry one.
  const bodyAllowed = ["POST", "PUT", "PATCH"].includes((ep.method || "POST").toUpperCase());
  const set = (next: RestParam[]) => onPatch({ params: next });
  const patchAt = (i: number, up: Partial<RestParam>) => set(params.map((p, j) => (j === i ? { ...p, ...up } : p)));
  const removeAt = (i: number) => set(params.filter((_, j) => j !== i));
  const add = () => set([...params, { name: "", location: "query", type: "string", required: false, maps_to: null }]);

  // A required Query input that no param feeds → the call 422s at runtime; warn (a param
  // feeds an input either by an explicit `maps_to` or, absent it, by the same public name).
  const fed = new Set(params.map((p) => p.maps_to || p.name).filter(Boolean));
  const missingRequired = (queryInputs ?? []).filter((q) => q.required && !fed.has(q.name)).map((q) => q.name);

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-muted-foreground/80">
        {t(
          "apps.restParamsDefine",
          "Definisci i parametri dell'endpoint (path · query · body) e mappa ognuno su un input della Query. Un parametro di path è un segnaposto {…} nell'URL.",
        )}
      </p>
      {placeholders.length > 0 && (
        <p className="text-[11px] text-muted-foreground/70">
          {t("apps.restParamsPathHint", "Segnaposto di path rilevati:")}{" "}
          <span className="font-mono">{placeholders.map((ph) => `{${ph}}`).join(" ")}</span>
        </p>
      )}
      <p className="text-[11px] text-muted-foreground/60">
        {bodyAllowed
          ? t("apps.restBodyHint", "I parametri «body» formano il corpo JSON della richiesta.")
          : t("apps.restBodyDisabled", "Il metodo GET/DELETE non ha corpo — usa POST/PUT/PATCH per i parametri «body».")}
      </p>
      {params.length === 0 ? (
        <p className="text-[11px] text-muted-foreground/60">{t("apps.restNoParams", "Nessun parametro.")}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {params.map((p, i) => {
            const isPath = p.location === "path";
            // Placeholders still free to bind (not taken by another path param). We do NOT
            // add `p.name` — a stale name (its `{…}` left the URL) must drop out of the list.
            const freePh = placeholders.filter((ph) => !params.some((q, j) => j !== i && q.location === "path" && q.name === ph));
            const nameOpts = freePh;
            // Hide `body` unless the verb carries one (keep it if already selected so the
            // select doesn't break — it reads as invalid until the operator moves it).
            const locOpts = bodyAllowed || p.location === "body" ? PARAM_LOCATIONS : PARAM_LOCATIONS.filter((l) => l !== "body");
            return (
              <div key={i} className="flex flex-col gap-1 rounded border border-border/60 px-2 py-1.5">
                <div className="flex items-center gap-1.5">
                  {isPath ? (
                    <select
                      className="h-8 min-w-0 flex-1 rounded-md border border-border bg-surface px-1.5 font-mono text-xs"
                      value={p.name}
                      disabled={!canWrite}
                      onChange={(e) => patchAt(i, { name: e.target.value })}
                    >
                      <option value="">{t("apps.restPickPlaceholder", "— scegli segnaposto {…} —")}</option>
                      {nameOpts.map((ph) => (
                        <option key={ph} value={ph}>{`{${ph}}`}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      className="h-8 min-w-0 flex-1 font-mono text-xs"
                      placeholder={t("apps.restParamName", "nome")}
                      value={p.name}
                      disabled={!canWrite}
                      onChange={(e) => patchAt(i, { name: e.target.value })}
                    />
                  )}
                  <select
                    className="h-8 rounded-md border border-border bg-surface px-1.5 text-xs"
                    value={p.location}
                    disabled={!canWrite}
                    onChange={(e) => {
                      const loc = e.target.value as ParamLocation;
                      const up: Partial<RestParam> = { location: loc };
                      // Switching TO path: prefill the name from a free URL placeholder so the
                      // path contract lines up without a separate step.
                      if (loc === "path" && !placeholders.includes(p.name)) up.name = freePh[0] ?? "";
                      patchAt(i, up);
                    }}
                  >
                    {locOpts.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                  <select
                    className="h-8 rounded-md border border-border bg-surface px-1.5 text-xs"
                    value={p.type}
                    disabled={!canWrite}
                    onChange={(e) => patchAt(i, { type: e.target.value as RestParam["type"] })}
                  >
                    {PARAM_TYPES.map((ty) => (
                      <option key={ty} value={ty}>{ty}</option>
                    ))}
                  </select>
                  <label className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground" title={t("apps.restReqFull", "obbligatorio")}>
                    <input type="checkbox" checked={p.required} disabled={!canWrite} onChange={(e) => patchAt(i, { required: e.target.checked })} />
                    {t("apps.restReq", "obbl.")}
                  </label>
                  {canWrite && (
                    <button type="button" onClick={() => removeAt(i)} className="px-1 text-danger" title={t("common.delete", "Elimina")}>×</button>
                  )}
                </div>
                <div className="flex items-center gap-1.5 pl-1">
                  <span className="shrink-0 text-[10px] text-muted-foreground/70">{t("apps.restMapTo", "→ input Query")}</span>
                  {inputNames.length > 0 ? (
                    <select
                      className="h-7 w-44 rounded-md border border-border bg-surface px-1.5 text-[11px]"
                      value={p.maps_to ?? ""}
                      disabled={!canWrite}
                      onChange={(e) => patchAt(i, { maps_to: e.target.value || null })}
                    >
                      <option value="">{t("apps.restMapNone", "— nessuno —")}</option>
                      {inputNames.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      className="h-7 w-44 min-w-0 font-mono text-[11px]"
                      placeholder={t("apps.restMapInput", "input")}
                      value={p.maps_to ?? ""}
                      disabled={!canWrite}
                      onChange={(e) => patchAt(i, { maps_to: e.target.value || null })}
                    />
                  )}
                  <Input
                    className="h-7 min-w-0 flex-1 text-[11px]"
                    placeholder={t("apps.restParamDesc", "descrizione (doc endpoint)")}
                    value={p.description ?? ""}
                    disabled={!canWrite}
                    onChange={(e) => patchAt(i, { description: e.target.value || null })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {canWrite && (
        <button type="button" onClick={add} className="flex items-center gap-1 rounded px-1 py-0.5 text-[11px] text-accent hover:bg-accent/10">
          <Plus className="h-3 w-3" strokeWidth={2} /> {t("apps.restAddParam", "Aggiungi parametro")}
        </button>
      )}
      {missingRequired.length > 0 && (
        <p className="text-[11px] text-warning">
          {t("apps.restMissingRequired", "Input obbligatori della Query non mappati da nessun parametro:")}{" "}
          <span className="font-mono">{missingRequired.join(", ")}</span>
        </p>
      )}
      <InputMapEditor ep={ep} canWrite={canWrite} onPatch={onPatch} />
    </div>
  );
}

/** ADR-0057 Level 2 — the OPTIONAL request-mapping editor. A `{query_input: "{{ template }}"}`
 *  map evaluated over the raw HTTP bag (`$`) to build the Query's `ctx.input`: arbitrary
 *  renames / coercions / computed values / constants. When any row is set it REPLACES the
 *  per-param binding, so it's collapsed by default (advanced). */
function InputMapEditor({
  ep,
  canWrite,
  onPatch,
}: {
  ep: RestEndpoint;
  canWrite: boolean;
  onPatch: (up: Partial<RestEndpoint>) => void;
}) {
  const { t } = useTranslation();
  const map = ep.input_map ?? {};
  const rows = Object.entries(map);
  const [open, setOpen] = React.useState(rows.length > 0);
  // The map is order-sensitive to edit but a Record loses order on a key rename, so we hold
  // an ordered draft of [key, template] pairs and rebuild the Record on every change.
  const [draft, setDraft] = React.useState<[string, string][]>(rows);
  React.useEffect(() => {
    setDraft(Object.entries(ep.input_map ?? {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ep.id]);

  const commit = (next: [string, string][]) => {
    setDraft(next);
    // Drop empty-key rows; a fully-empty map persists as null (no transform).
    const rec: Record<string, string> = {};
    for (const [k, v] of next) if (k.trim()) rec[k.trim()] = v;
    onPatch({ input_map: Object.keys(rec).length ? rec : null });
  };
  const setAt = (i: number, k: string, v: string) => commit(draft.map((r, j) => (j === i ? [k, v] : r)));

  return (
    <div className="space-y-1.5 rounded-md border border-dashed border-border/60 p-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 text-left text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>›</span>
        {t("apps.restInputMap", "Trasforma input → Query (avanzato)")}
        {rows.length > 0 && <span className="rounded-full bg-accent/15 px-1.5 text-[10px] text-accent">{rows.length}</span>}
      </button>
      {open && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-muted-foreground/70">
            {t(
              "apps.restInputMapHint",
              "Opzionale. Ogni riga costruisce un input della Query da un'espressione {{ }} sui parametri ricevuti ($). Es. {{ $.amount * 100 }} · {{ $.first + ' ' + $.last }}. Se presente, sostituisce la mappatura automatica.",
            )}
          </p>
          {draft.map(([k, v], i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Input
                className="h-7 w-36 min-w-0 font-mono text-[11px]"
                placeholder={t("apps.restInputMapKey", "input Query")}
                value={k}
                disabled={!canWrite}
                onChange={(e) => setAt(i, e.target.value, v)}
              />
              <span className="text-muted-foreground">=</span>
              <Input
                className="h-7 min-w-0 flex-1 font-mono text-[11px]"
                placeholder="{{ $.campo }}"
                value={v}
                disabled={!canWrite}
                onChange={(e) => setAt(i, k, e.target.value)}
              />
              {canWrite && (
                <button type="button" onClick={() => commit(draft.filter((_, j) => j !== i))} className="px-1 text-danger" title={t("common.delete", "Elimina")}>×</button>
              )}
            </div>
          ))}
          {canWrite && (
            <button
              type="button"
              onClick={() => commit([...draft, ["", ""]])}
              className="flex items-center gap-1 rounded px-1 py-0.5 text-[11px] text-accent hover:bg-accent/10"
            >
              <Plus className="h-3 w-3" strokeWidth={2} /> {t("apps.restInputMapAdd", "Aggiungi trasformazione")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// -- OpenAPI reference (Scalar/Swagger-style) --------------------------------

type JsonSchema = {
  type?: string;
  properties?: Record<string, { type?: string; description?: string }>;
  required?: string[];
  items?: JsonSchema;
};
type OpenApiOperation = {
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: { name: string; in: string; required?: boolean; description?: string; schema?: { type?: string } }[];
  requestBody?: { content?: Record<string, { schema?: JsonSchema }> };
  responses?: Record<string, { description?: string; content?: Record<string, { schema?: JsonSchema }> }>;
};
type FlatOp = { anchor: string; method: string; path: string; tag: string; op: OpenApiOperation };

function flattenOps(paths: Record<string, Record<string, OpenApiOperation>>): FlatOp[] {
  const out: FlatOp[] = [];
  for (const [path, ops] of Object.entries(paths)) {
    for (const [method, op] of Object.entries(ops)) {
      const tag = (op.tags && op.tags[0]) || "Generale";
      out.push({ anchor: `op-${method}-${path}`.replace(/[^a-zA-Z0-9_-]/g, "_"), method: method.toUpperCase(), path, tag, op });
    }
  }
  return out;
}

/** The live OpenAPI reference — Scalar-style: a searchable left nav (grouped by
 *  resource) that scrolls the main docs to the clicked operation. */
/**
 * The live OpenAPI reference for the REST interface. Split into a `list` view
 * (search + endpoint nav) and a `detail` view (the reference body) so it plugs
 * into the builder's STANDARD sidebar/content grid — same shape as `appRest`
 * (RestInterfaceEditor) — instead of a bespoke self-contained two-column card.
 * The selected anchor is lifted to the caller so the nav (sidebar) can scroll
 * the body (content). `full` keeps the standalone two-column layout for any
 * caller that renders it on its own.
 */
export function RestApiDocs({
  appId,
  view = "full",
  selectedAnchor,
  onSelectAnchor,
}: {
  appId: string;
  view?: "list" | "detail" | "full";
  selectedAnchor?: string | null;
  onSelectAnchor?: (anchor: string) => void;
}) {
  const { t } = useTranslation();
  const rest = useRest(appId);
  const openapiQ = useQuery({ queryKey: ["apps", appId, "openapi"], queryFn: () => appsApi.getOpenapi(appId), enabled: !!rest.iface });
  const [search, setSearch] = React.useState("");
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const ops = React.useMemo(() => flattenOps((openapiQ.data?.paths as never) ?? {}), [openapiQ.data]);
  const q = search.trim().toLowerCase();
  const visible = q ? ops.filter((o) => `${o.method} ${o.path} ${o.op.summary ?? ""} ${o.tag}`.toLowerCase().includes(q)) : ops;

  // Group by tag (resource), preserving the doc's tag order.
  const tagOrder = ((openapiQ.data?.tags as { name: string }[] | undefined) ?? []).map((x) => x.name);
  const groups = React.useMemo(() => {
    const byTag = new Map<string, FlatOp[]>();
    for (const o of visible) (byTag.get(o.tag) ?? byTag.set(o.tag, []).get(o.tag)!).push(o);
    const keys = [...byTag.keys()].sort((a, b) => {
      const ia = tagOrder.indexOf(a), ib = tagOrder.indexOf(b);
      return (ia < 0 ? 1e9 : ia) - (ib < 0 ? 1e9 : ib);
    });
    return keys.map((tag) => ({ tag, ops: byTag.get(tag)! }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, openapiQ.data]);

  const scrollTo = React.useCallback((anchor: string) => {
    const el = scrollRef.current?.querySelector(`#${CSS.escape(anchor)}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Detail view: when the caller picks an endpoint in the sidebar nav, scroll to it.
  React.useEffect(() => {
    if (view === "detail" && selectedAnchor) scrollTo(selectedAnchor);
  }, [view, selectedAnchor, scrollTo, groups]);

  // The endpoint nav (search + resource-grouped list) — bare, so it inherits the
  // host sidebar's chrome (border-r + padding) exactly like the other list panels.
  const navEl = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex items-center gap-1.5 rounded-md border border-border bg-surface px-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
        <input className="h-8 w-full bg-transparent text-xs outline-none" placeholder={t("apps.restSearch", "Cerca endpoint…")} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {ops.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-muted-foreground">{t("apps.restNoPaths", "Nessun percorso esposto — salva un endpoint.")}</p>
        ) : (
          groups.map((g) => (
            <div key={g.tag} className="mt-1">
              <div className="px-1 py-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70">{g.tag}</div>
              {g.ops.map((o) => (
                <button
                  key={o.anchor}
                  type="button"
                  onClick={() => (onSelectAnchor ? onSelectAnchor(o.anchor) : scrollTo(o.anchor))}
                  className={`mb-0.5 flex w-full items-center gap-1.5 rounded px-2 py-1 text-left hover:bg-surface ${selectedAnchor === o.anchor ? "bg-surface" : ""}`}
                >
                  <span className={`shrink-0 rounded px-1 text-[8px] font-bold uppercase ${METHOD_TINT[o.method] ?? "bg-muted text-muted-foreground"}`}>{o.method}</span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px]">{o.path.replace(/^\/api\/apps\/[^/]+/, "")}</span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // The reference body.
  const bodyEl = (
    <div ref={scrollRef} className="min-h-0 overflow-y-auto pr-2">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold">
        <BookText className="h-5 w-5 text-muted-foreground" strokeWidth={1.75} /> {openapiQ.data?.info?.title ?? t("apps.restOpenapi", "Documentazione API")}
      </h2>
      <p className="mb-6 text-xs text-muted-foreground">{t("apps.restBaseNote", "Base:")} <span className="font-mono">/api/apps/{"{slug}"}</span></p>
      {groups.map((g) => (
        <div key={g.tag} className="mb-8">
          <h3 className="mb-3 border-b border-border pb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{g.tag}</h3>
          <div className="space-y-6">
            {g.ops.map((o) => (
              <OperationCard key={o.anchor} op={o} />
            ))}
          </div>
        </div>
      ))}
      {ops.length === 0 && <p className="text-sm text-muted-foreground">{t("apps.restNoPaths", "Nessun percorso esposto — salva un endpoint.")}</p>}
    </div>
  );

  if (!rest.iface) return <div className="p-4 text-sm text-muted-foreground">{t("apps.restNoIface", "Crea prima l'interfaccia REST dall'editor.")}</div>;
  if (openapiQ.isLoading) return <div className="p-4 text-sm text-muted-foreground">…</div>;

  if (view === "list") return navEl;
  if (view === "detail") return bodyEl;
  // `full` — standalone two-column layout.
  return (
    <div className="grid h-full min-h-[520px] grid-cols-[16rem_1fr] gap-4">
      <div className="flex min-h-0 flex-col rounded-lg border border-border p-2">{navEl}</div>
      {bodyEl}
    </div>
  );
}

/** A small type table for a JSON-schema object (input/output field types). */
function SchemaTable({ props, required }: { props: [string, { type?: string; description?: string }][]; required?: Set<string> }) {
  const { t } = useTranslation();
  return (
    <table className="w-full text-left text-xs">
      <tbody>
        {props.map(([name, sc]) => (
          <tr key={name} className="border-t border-border/50 align-top">
            <td className="py-1 pr-3 font-mono">{name}</td>
            <td className="py-1 pr-3 text-muted-foreground">{sc.type ?? "string"}</td>
            {required && (
              <td className="py-1 pr-3 text-muted-foreground">
                {required.has(name) ? t("apps.restReqFull", "obbligatorio") : t("apps.restOpt", "opzionale")}
              </td>
            )}
            <td className="py-1 text-muted-foreground/80">{sc.description ?? ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A Swagger-style operation — an accordion header + (on expand) the parameter /
 *  body / response TYPES and a "Prova" panel that fires the request live. */
function OperationCard({ op }: { op: FlatOp }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const bodySchema = op.op.requestBody?.content?.["application/json"]?.schema;
  const bodyProps = Object.entries(bodySchema?.properties ?? {});
  const bodyReq = new Set(bodySchema?.required ?? []);
  const params = op.op.parameters ?? [];
  const pathParams = params.filter((p) => p.in === "path");
  const queryParams = params.filter((p) => p.in === "query");
  const hasBody = bodyProps.length > 0;
  const relPath = op.path.replace(/^\/api\/apps\/[^/]+/, "");

  // 200 response schema (OUTPUT types) — `list` gives an array, else an object.
  const okSchema = op.op.responses?.["200"]?.content?.["application/json"]?.schema;
  const rowSchema = okSchema?.type === "array" ? okSchema.items : okSchema;
  const respProps = Object.entries(rowSchema?.properties ?? {});

  // --- Try it out state -------------------------------------------------------
  const [pathVals, setPathVals] = React.useState<Record<string, string>>({});
  const [queryVals, setQueryVals] = React.useState<Record<string, string>>({});
  const [bodyText, setBodyText] = React.useState(() =>
    hasBody ? JSON.stringify(Object.fromEntries(bodyProps.map(([n]) => [n, ""])), null, 2) : "",
  );
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState<{ status: number; body: string } | null>(null);
  const [runErr, setRunErr] = React.useState<string | null>(null);
  const token = useAuthStore((s) => s.token);

  const execute = async () => {
    setRunning(true);
    setResult(null);
    setRunErr(null);
    try {
      // Fill {param} placeholders in the FULL path (it already carries the slug).
      let url = op.path;
      for (const p of pathParams) url = url.replace(`{${p.name}}`, encodeURIComponent(pathVals[p.name] ?? ""));
      const qs = new URLSearchParams();
      for (const p of queryParams) {
        const v = queryVals[p.name];
        if (v !== undefined && v !== "") qs.set(p.name, v);
      }
      const full = qs.toString() ? `${url}?${qs}` : url;
      const init: RequestInit = {
        method: op.method,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      };
      if (hasBody && op.method !== "GET" && bodyText.trim()) init.body = bodyText;
      const resp = await fetch(full, init);
      const text = await resp.text();
      let pretty = text;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        /* not JSON — show raw */
      }
      setResult({ status: resp.status, body: pretty });
    } catch (e) {
      setRunErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div id={op.anchor} className="scroll-mt-4 overflow-hidden rounded-lg border border-border">
      {/* Accordion header — click to expand. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-surface"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2} />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2} />
        )}
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${METHOD_TINT[op.method] ?? "bg-muted text-muted-foreground"}`}>{op.method}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-sm">{relPath}</span>
        {op.op.summary && <span className="hidden truncate text-xs text-muted-foreground sm:inline">{op.op.summary}</span>}
      </button>

      {open && (
        <div className="border-t border-border p-3">
          {op.op.description && <p className="mb-3 text-xs text-muted-foreground">{op.op.description}</p>}

          {params.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{t("apps.restDocParams", "Parametri")}</div>
              <table className="w-full text-left text-xs">
                <tbody>
                  {params.map((p) => (
                    <tr key={`${p.in}-${p.name}`} className="border-t border-border/50 align-top">
                      <td className="py-1 pr-3 font-mono">{p.name}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{p.in}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{p.schema?.type ?? "string"}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{p.required ? t("apps.restReqFull", "obbligatorio") : t("apps.restOpt", "opzionale")}</td>
                      <td className="py-1 text-muted-foreground/80">{p.description ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasBody && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{t("apps.restDocBody", "Corpo (JSON)")}</div>
              <SchemaTable props={bodyProps} required={bodyReq} />
            </div>
          )}

          {respProps.length > 0 && (
            <div className="mb-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {t("apps.restDocResponse", "Risposta")} · 200 {okSchema?.type === "array" ? t("apps.restArrayOf", "(array di)") : ""}
              </div>
              <SchemaTable props={respProps} />
            </div>
          )}

          {/* --- Try it out --- */}
          <div className="mt-3 rounded-md border border-border bg-surface/40 p-2.5">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{t("apps.restTryIt", "Prova")}</div>
            {pathParams.map((p) => (
              <label key={p.name} className="mb-1.5 flex flex-col gap-0.5">
                <span className="font-mono text-[11px] text-muted-foreground">{p.name} <span className="text-muted-foreground/60">(path)</span></span>
                <Input value={pathVals[p.name] ?? ""} onChange={(e) => setPathVals((s) => ({ ...s, [p.name]: e.target.value }))} className="h-7 text-xs" />
              </label>
            ))}
            {queryParams.map((p) => (
              <label key={p.name} className="mb-1.5 flex flex-col gap-0.5">
                <span className="font-mono text-[11px] text-muted-foreground">{p.name} <span className="text-muted-foreground/60">(query)</span></span>
                <Input value={queryVals[p.name] ?? ""} onChange={(e) => setQueryVals((s) => ({ ...s, [p.name]: e.target.value }))} className="h-7 text-xs" />
              </label>
            ))}
            {hasBody && op.method !== "GET" && (
              <label className="mb-1.5 flex flex-col gap-0.5">
                <span className="font-mono text-[11px] text-muted-foreground">body (JSON)</span>
                <textarea
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  rows={5}
                  className="rounded border border-border bg-background px-2 py-1.5 font-mono text-[11px]"
                />
              </label>
            )}
            <button
              type="button"
              onClick={() => void execute()}
              disabled={running}
              className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
            >
              <Play className="h-3.5 w-3.5" strokeWidth={2} /> {running ? t("apps.restRunning", "Esecuzione…") : t("apps.restExecute", "Esegui")}
            </button>

            {runErr && <p className="mt-2 text-[11px] text-danger">{runErr}</p>}
            {result && (
              <div className="mt-2">
                <div className="mb-1 flex items-center gap-2 text-[11px]">
                  <span className={`rounded px-1.5 py-0.5 font-bold ${result.status < 300 ? "bg-success/15 text-success" : result.status < 500 ? "bg-warning/15 text-warning" : "bg-danger/15 text-danger"}`}>
                    {result.status}
                  </span>
                  <span className="text-muted-foreground">{t("apps.restResponse", "Risposta")}</span>
                </div>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-muted/40 p-2 font-mono text-[10px]">{result.body}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ""}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-muted-foreground/70">{hint}</span>}
    </label>
  );
}
